import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdCheck, MdClose } from "react-icons/md";
import { EButtonKind } from "src/components/Button/Button.types";
import CollapsibleBlock from "src/components/CollapsibleBlock/CollapsibleBlock";
import ConfirmDialog from "src/components/ConfirmDialog/ConfirmDialog";
import IconSelector from "src/components/IconSelector/IconSelector";
import Modal from "src/components/Modal/Modal";
import CustomSelect from "src/components/Select/CustomSelect";
import TextInput from "src/components/TextInput/TextInput";
import { ICON_SIZES } from "src/constants/icons";
import { ASK_CONFIRMATION_ON_SAVE_OR_UPDATE } from "src/helpers/constants";

export enum EContactType {
  PHONE = "PHONE",
  EMAIL = "EMAIL",
  LINK = "LINK",
  SMS = "SMS",
}

export interface IContactDetailsData {
  type: EContactType;
  value: string;
  name: string;
  icon: string;
  notes?: string;
}

interface IAddContactDetailsModal {
  onClose: () => void;
  onConfirm: (data: IContactDetailsData) => void;
  isLoading: boolean;
  isEditMode?: boolean;
  dataToEdit?: IContactDetailsData;
}

const AddContactDetailsModal: React.FC<IAddContactDetailsModal> = ({
  onClose,
  onConfirm,
  isLoading,
  isEditMode = false,
  dataToEdit = {
    type: EContactType.PHONE,
    value: "",
    name: "",
    icon: "",
    notes: "",
  },
}) => {
  const { t } = useTranslation(["common"]);
  const [contactType, setContactType] = useState<EContactType>(
    EContactType.PHONE,
  );
  const [contactName, setContactName] = useState<string>("");
  const [contactData, setContactData] = useState<string>("");
  const [contactIcon, setContactIcon] = useState<string>("");
  const [contactNotes, setContactNotes] = useState<string>("");
  const [showSaveConfirmDialog, setShowSaveConfirmDialog] =
    useState<boolean>(false);

  const contactDataFields = useMemo(() => {
    switch (contactType) {
      case EContactType.EMAIL: {
        return {
          valuePlaceholder: "Digita la dirección de correo electrónico",
          fieldType: "email",
          valueLabel: "Correo electrónico",
          shortNameLabel: "Nombre corto",
          shortNamePlaceholder: "Correo electrónico, enviar correo, etc.",
        };
      }
      case EContactType.LINK: {
        return {
          valuePlaceholder:
            "Digita o pega la dirección web (puedes agregar el enlace para contactar al usuario por WhatsApp, enlace de Google Maps, etc.)",
          fieldType: "url",
          valueLabel: "Dirección web",
          shortNameLabel: "Nombre corto",
          shortNamePlaceholder: "Página web, Google Maps, WhatsApp, etc.",
        };
      }
      case EContactType.PHONE: {
        return {
          valuePlaceholder: "Digita el número de teléfono",
          fieldType: "phone",
          valueLabel: "Número telefónico",
          shortNameLabel: "Nombre corto",
          shortNamePlaceholder: "Teléfono principal, móvil, oficina, etc.",
        };
      }
      case EContactType.SMS: {
        return {
          valuePlaceholder: "Digita el número de teléfono",
          fieldType: "phone",
          valueLabel: "Número telefónico",
          shortNameLabel: "Nombre corto",
          shortNamePlaceholder: "Teléfono principal, móvil, oficina, etc.",
        };
      }

      default: {
        return null;
      }
    }
  }, [contactType]);

  const canSave = useMemo(() => {
    if (isEditMode) {
      const { type, value, name, icon, notes } = dataToEdit;
      const allFilled =
        !!contactType && !!contactName && !!contactData && !!contactIcon;
      const allEqual =
        type === contactType &&
        value === contactData &&
        name === contactName &&
        icon === contactIcon &&
        notes === contactNotes;

      return allFilled && !allEqual;
    }

    return !!contactType && !!contactName && !!contactData && !!contactIcon;
  }, [
    contactType,
    contactName,
    contactData,
    contactIcon,
    contactNotes,
    isEditMode,
    dataToEdit,
  ]);

  const closeModal = useCallback(() => {
    if (!isLoading) {
      onClose();
    }
  }, [isLoading]);

  const saveContactData = () => {
    onConfirm({
      type: contactType,
      value: contactData,
      name: contactName,
      icon: contactIcon,
      notes: contactNotes,
    });
  };

  useEffect(() => {
    if (isEditMode) {
      const { type, value, name, icon, notes = "" } = dataToEdit;

      setContactName(name);
      setContactType(type);
      setContactData(value);
      setContactIcon(icon);
      setContactNotes(notes);
    }
  }, []);

  return (
    <>
      <Modal
        onEscape={() => closeModal()}
        className={clsx(["md:!max-w-[550px]", "md:!w-full"])}
      >
        <Modal.Header
          title={
            isEditMode
              ? "Editar información de contacto"
              : "Agregar información de contacto"
          }
          onClose={() => closeModal()}
          isClosable={!isLoading}
        />
        <Modal.Body>
          <div className={clsx(["flex", "flex-col", "gap-4", "w-full"])}>
            <CollapsibleBlock title="Información" blocked open>
              <div className={clsx(["flex", "w-full", "gap-4", "flex-col"])}>
                <div
                  className={clsx([
                    "grid",
                    "grid-cols-1",
                    "md:grid-cols-2",
                    "gap-4",
                  ])}
                >
                  <CustomSelect
                    onChange={(e: any) => setContactType(e?.value)}
                    isClearable={false}
                    isSearchable={false}
                    useValueAsLabel={false}
                    required
                    options={[
                      {
                        value: EContactType.LINK,
                        label: "Dirección web (URL)",
                      },
                      {
                        value: EContactType.EMAIL,
                        label: "Correo electrónico",
                      },
                      {
                        value: EContactType.PHONE,
                        label: "Teléfono",
                      },
                      {
                        value: EContactType.SMS,
                        label: "Enviar SMS",
                      },
                    ]}
                    value={contactType}
                    label="Tipo"
                    placeholder="Selecciona el tipo de contacto"
                  />
                  <TextInput
                    value={contactName}
                    onChange={(e) => setContactName(e?.target?.value)}
                    placeholder={contactDataFields?.shortNamePlaceholder}
                    label={contactDataFields?.shortNameLabel}
                    required
                    id="contact-name-field"
                  />
                </div>
                <div
                  className={clsx([
                    "grid",
                    "grid-cols-1",
                    "md:grid-cols-2",
                    "gap-4",
                  ])}
                >
                  {contactType !== EContactType.LINK && (
                    <TextInput
                      value={contactData}
                      onChange={(e) => setContactData(e?.target?.value)}
                      placeholder={contactDataFields?.valuePlaceholder}
                      label={contactDataFields?.valueLabel}
                      required
                    />
                  )}
                  {contactType === EContactType.LINK && (
                    <TextInput
                      value={contactData}
                      onChange={(e) => setContactData(e?.target?.value)}
                      placeholder={contactDataFields?.valuePlaceholder}
                      label={contactDataFields?.valueLabel}
                      required
                    />
                  )}
                  <TextInput
                    value={contactNotes}
                    onChange={(e) => setContactNotes(e?.target?.value)}
                    placeholder={""}
                    label={"Notas"}
                  />
                </div>
              </div>
            </CollapsibleBlock>
            <IconSelector
              onClick={(e: any) => setContactIcon(e)}
              selected={contactIcon}
              desktopColumns={4}
            />
          </div>
        </Modal.Body>
        <Modal.Footer
          actions={[
            {
              text: t(
                isEditMode ? "common:buttons.update" : "common:buttons.add",
              ),
              onClick: () => {
                if (ASK_CONFIRMATION_ON_SAVE_OR_UPDATE) {
                  setShowSaveConfirmDialog(true);
                } else {
                  saveContactData();
                }
              },
              kind: EButtonKind.SAVE,
              icon: <MdCheck size={ICON_SIZES.MD} />,
              disabled: !canSave || isLoading,
              isLoading,
            },
            {
              text: t("common:buttons.close"),
              onClick: () => closeModal(),
              kind: EButtonKind.NORMAL,
              icon: <MdClose size={ICON_SIZES.MD} />,
              disabled: isLoading,
            },
          ]}
        />
      </Modal>
      {showSaveConfirmDialog && (
        <ConfirmDialog
          title={isEditMode ? "Editar" : "Agregar"}
          description={
            isEditMode
              ? "¿Deseas actualizar la información de contacto?"
              : "¿Deseas agregar la nueva información de contacto?"
          }
          onClose={() => {
            setShowSaveConfirmDialog(false);
          }}
          onConfirm={async () => {
            saveContactData();
          }}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default AddContactDetailsModal;
